import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Pencil, Trash2, Lock } from "lucide-react";

const EMPTY = { id: null, name: "", description: "", permissions: [] };

export function RolesManager({ onRolesChanged }) {
  const [builtin, setBuiltin] = useState([]);
  const [custom, setCustom] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
    setBuiltin(r.data.builtin); setCustom(r.data.custom); setCatalog(p.data.permissions);
    onRolesChanged && onRolesChanged();
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (role) => { setForm({ id: role.id, name: role.name, description: role.description || "", permissions: [...(role.permissions || [])] }); setOpen(true); };
  const toggle = (key) => setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((k) => k !== key) : [...f.permissions, key] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Role name is required"); return; }
    try {
      if (form.id) await api.patch(`/roles/${form.id}`, { name: form.name, description: form.description, permissions: form.permissions });
      else await api.post("/roles", { name: form.name, description: form.description, permissions: form.permissions });
      toast.success(form.id ? "Role updated" : "Role created"); setOpen(false); await load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (role) => { try { await api.delete(`/roles/${role.id}`); toast.success("Role deleted"); await load(); } catch (e) { toast.error(apiErr(e)); } };

  const labelFor = (k) => (catalog.find((c) => c.key === k) || {}).label || k;

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500"><Lock size={13} /> Built-in roles</div>
        <div className="grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-2 lg:grid-cols-4">
          {builtin.map((r) => (
            <div key={r.id} className="bg-white p-4" data-testid={`builtin-role-${r.id}`}>
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /><span className="text-sm font-bold text-zinc-900">{r.name}</span></div>
              <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">{r.isAdmin ? "Full access to everything, including settings & role management." : (r.permissions.length ? r.permissions.map(labelFor).join(" · ") : "Read-only directory access.")}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500"><ShieldCheck size={13} /> Custom roles</div>
          <Button onClick={openNew} size="sm" data-testid="new-role-btn" className="rounded-sm bg-primary hover:bg-[#002480]"><Plus size={15} className="mr-1.5" /> New role</Button>
        </div>
        {custom.length === 0 ? (
          <div className="border border-dashed border-zinc-300 bg-white p-8 text-center text-sm text-zinc-400">No custom roles yet. Create one to grant a tailored set of permissions.</div>
        ) : (
          <div className="border border-zinc-200 bg-white">
            {custom.map((r) => (
              <div key={r.id} className="flex items-start justify-between gap-4 border-b border-zinc-100 p-4 last:border-0" data-testid={`custom-role-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-900">{r.name}</div>
                  {r.description && <div className="text-xs text-zinc-500">{r.description}</div>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(r.permissions || []).map((k) => <span key={k} className="border border-primary/20 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary">{labelFor(k)}</span>)}
                    {(r.permissions || []).length === 0 && <span className="text-[11px] text-zinc-400">No permissions assigned</span>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => openEdit(r)} data-testid={`edit-role-${r.id}`} className="p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><Pencil size={15} /></button>
                  <button onClick={() => del(r)} data-testid={`delete-role-${r.id}`} className="p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col rounded-sm sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{form.id ? "Edit role" : "New custom role"}</DialogTitle>
            <DialogDescription>Define a name and the set of permissions this role grants.</DialogDescription></DialogHeader>
          <div className="-mr-2 flex-1 space-y-4 overflow-y-auto py-2 pr-2">
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Role name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" className="rounded-sm border-zinc-300" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-desc" className="rounded-sm border-zinc-300" /></div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Permissions</Label>
              <div className="grid grid-cols-1 gap-2 rounded-sm border border-zinc-200 p-3">
                {catalog.map((p) => (
                  <label key={p.key} className="flex cursor-pointer items-start gap-3 text-sm" data-testid={`perm-${p.key}`}>
                    <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => toggle(p.key)} className="mt-0.5" />
                    <span><span className="font-medium text-zinc-800">{p.label}</span><span className="block font-mono text-[10px] text-zinc-400">{p.key}</span></span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="border-t border-zinc-100 pt-3">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-role" className="rounded-sm bg-primary hover:bg-[#002480]">{form.id ? "Save changes" : "Create role"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
